import { useState, useEffect, useRef, useCallback } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { useAuth } from '../contexts/AuthContext';
import { Conversation } from '../types/conversation';
import { SupportMessage } from '../types/supportMessage';
import {
  normalizeConversation,
  normalizeSupportMessage,
  rememberHubSupportMessageId,
} from '../utils/supportHubPayload';

export interface ConversationHubCallbacks {
  onMessageReceived?: (message: SupportMessage) => void;
  onMessageStatusChanged?: (payload: MessageStatusChangedPayload) => void;
  onConversationListChanged?: (payload: { conversationId: string }) => void;
  onConversationUpdated?: (conversation: Conversation) => void;
  onConversationDeleted?: (payload: { conversationId: string }) => void;
  onConversationAssigned?: (conversation: Conversation) => void;
  onTypingIndicator?: (payload: { conversationId: string; userId: string; isTyping: boolean }) => void;
}

export interface MessageStatusChangedPayload {
  conversationId: string;
  messageId: string;
  status: string;
  error?: string | null;
  outcomeUnknown?: boolean;
  contentAttributes?: string | null;
}

function getCustomerId(): string {
  return sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';
}

function isConnectionClosedError(error: unknown): boolean {
  if (!(error instanceof Error))
  {
    return false;
  }

  const message = error.message.toLowerCase();
  return message.includes('connection being closed')
    || message.includes('invocation canceled')
    || message.includes('not in the connected state');
}

function readMessageStatusChanged(payload: unknown): MessageStatusChangedPayload | null {
  if (!payload || typeof payload !== 'object')
  {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const conversationId = String(record.conversationId ?? record.ConversationId ?? '');
  const messageId = String(record.messageId ?? record.MessageId ?? '');
  const status = String(record.status ?? record.Status ?? '');
  if (!conversationId || !messageId || !status)
  {
    return null;
  }

  const contentAttributesRaw = record.contentAttributes ?? record.ContentAttributes;
  const errorRaw = record.error ?? record.Error;
  const outcomeUnknownRaw = record.outcomeUnknown ?? record.OutcomeUnknown;

  return {
    conversationId,
    messageId,
    status,
    error: typeof errorRaw === 'string' ? errorRaw : null,
    outcomeUnknown: outcomeUnknownRaw === true,
    contentAttributes: typeof contentAttributesRaw === 'string' ? contentAttributesRaw : null,
  };
}

async function safeInvoke(conn: HubConnection, method: string, ...args: unknown[]): Promise<void> {
  if (conn.state !== HubConnectionState.Connected)
  {
    return;
  }

  try
  {
    await conn.invoke(method, ...args);
  }
  catch (error)
  {
    if (isConnectionClosedError(error))
    {
      return;
    }

    throw error;
  }
}

export const useConversationHub = (
  options?: {
    conversationId?: string | null;
    inboxId?: string | null;
    callbacks?: ConversationHubCallbacks;
  }
) => {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const { user } = useAuth();
  const connectionRef = useRef<HubConnection | null>(null);
  const callbacksRef = useRef(options?.callbacks);
  const conversationIdRef = useRef(options?.conversationId);
  const inboxIdRef = useRef(options?.inboxId);
  const joinedConversationIdRef = useRef<string | null>(null);

  callbacksRef.current = options?.callbacks;
  conversationIdRef.current = options?.conversationId;
  inboxIdRef.current = options?.inboxId;

  const stopConnection = useCallback(async () => {
    if (connectionRef.current)
    {
      try
      {
        await connectionRef.current.stop();
      }
      catch
      {
        // ignore stop errors during teardown
      }
      connectionRef.current = null;
      joinedConversationIdRef.current = null;
      setConnection(null);
      setIsConnected(false);
    }
  }, []);

  const joinGroups = useCallback(async (conn: HubConnection) => {
    const customerId = getCustomerId();
    if (customerId)
    {
      await safeInvoke(conn, 'JoinCustomerGroup', customerId);
    }

    const inboxId = inboxIdRef.current;
    if (inboxId)
    {
      await safeInvoke(conn, 'JoinInboxGroup', inboxId);
    }

    const conversationId = conversationIdRef.current;
    const previousConversationId = joinedConversationIdRef.current;

    if (previousConversationId
      && previousConversationId.toLowerCase() !== (conversationId ?? '').toLowerCase())
    {
      await safeInvoke(conn, 'LeaveConversationGroup', previousConversationId);
      joinedConversationIdRef.current = null;
    }

    if (conversationId)
    {
      await safeInvoke(conn, 'JoinConversationGroup', conversationId);
      joinedConversationIdRef.current = conversationId;
    }
  }, []);

  const createConnection = useCallback(async () => {
    if (!user?.id)
    {
      return;
    }

    await stopConnection();

    try
    {
      const hubBaseUrl = process.env.REACT_APP_CONVERSATION_HUB_URL
        || process.env.REACT_APP_HUB_URL
        || 'https://localhost:7180';
      const hubUrl = `${hubBaseUrl}/hubs/conversations`;
      const accessToken = sessionStorage.getItem('@KnowInDoc:accessToken') ?? '';

      const newConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          withCredentials: true,
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect([5000, 15000, 30000, 60000])
        .configureLogging(
          process.env.NODE_ENV === 'development' ? LogLevel.Warning : LogLevel.Error
        )
        .build();

      newConnection.on('MessageReceived', (payload: unknown) => {
        const message = normalizeSupportMessage(payload);
        if (message && rememberHubSupportMessageId(message.id))
        {
          callbacksRef.current?.onMessageReceived?.(message);
        }
      });

      newConnection.on('MessageStatusChanged', (payload: unknown) => {
        const statusChanged = readMessageStatusChanged(payload);
        if (statusChanged)
        {
          callbacksRef.current?.onMessageStatusChanged?.(statusChanged);
        }
      });

      newConnection.on('ConversationListChanged', (payload: { conversationId?: string; ConversationId?: string }) => {
        const conversationId = payload.conversationId ?? payload.ConversationId;
        if (conversationId)
        {
          callbacksRef.current?.onConversationListChanged?.({ conversationId });
        }
      });

      newConnection.on('ConversationUpdated', (payload: unknown) => {
        const conversation = normalizeConversation(payload);
        if (conversation)
        {
          callbacksRef.current?.onConversationUpdated?.(conversation);
        }
      });

      newConnection.on('ConversationDeleted', (payload: { conversationId?: string; ConversationId?: string }) => {
        const conversationId = payload.conversationId ?? payload.ConversationId;
        if (conversationId)
        {
          callbacksRef.current?.onConversationDeleted?.({ conversationId });
        }
      });

      newConnection.on('ConversationAssigned', (payload: unknown) => {
        const conversation = normalizeConversation(payload);
        if (conversation)
        {
          callbacksRef.current?.onConversationAssigned?.(conversation);
        }
      });

      newConnection.on('TypingIndicator', (payload: { conversationId: string; userId: string; isTyping: boolean }) => {
        callbacksRef.current?.onTypingIndicator?.(payload);
      });

      newConnection.onreconnected(async () => {
        setIsConnected(true);
        setConnectionError(null);
        await joinGroups(newConnection);
      });

      newConnection.onclose(() => {
        setIsConnected(false);
      });

      await newConnection.start();
      await joinGroups(newConnection);

      connectionRef.current = newConnection;
      setConnection(newConnection);
      setIsConnected(true);
      setConnectionError(null);
    }
    catch (error: unknown)
    {
      if (isConnectionClosedError(error))
      {
        return;
      }

      const message = error instanceof Error ? error.message : 'Falha ao conectar ao hub de conversas';
      setConnectionError(message);
      setIsConnected(false);
      if (process.env.NODE_ENV === 'development')
      {
        console.warn('[ConversationHub] Não foi possível conectar:', message);
      }
    }
  }, [user?.id, stopConnection, joinGroups]);

  useEffect(() => {
    if (user?.id)
    {
      void createConnection();
    }
    else
    {
      void stopConnection();
    }

    return () => {
      void stopConnection();
    };
  }, [user?.id, createConnection, stopConnection]);

  useEffect(() => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== HubConnectionState.Connected)
    {
      return;
    }

    void joinGroups(conn).catch(error => {
      if (isConnectionClosedError(error))
      {
        return;
      }

      if (process.env.NODE_ENV === 'development')
      {
        console.warn('[ConversationHub] Falha ao entrar nos grupos:', error);
      }
    });
  }, [options?.conversationId, options?.inboxId, isConnected, joinGroups]);

  const sendTypingIndicator = useCallback(async (conversationId: string, isTyping: boolean) => {
    const conn = connectionRef.current;
    if (!conn || conn.state !== HubConnectionState.Connected)
    {
      return;
    }

    await safeInvoke(conn, 'SendTypingIndicator', conversationId, isTyping);
  }, []);

  return {
    connection,
    isConnected,
    connectionError,
    sendTypingIndicator,
  };
};
